import { useQuery } from "@tanstack/react-query"
import { ART_ETIKETT, tagMonat, wissensdatenbankHref, type Fakt } from "@/lib/betriebswissen"

/**
 * „Wissen zum Betrieb" in der Metaspalte der Mail (Paul, 01.10.2026: „als
 * Tooltip bei den Mails angezeigt, so wie Leistungsspektrum … und als
 * Extra-Reiter ‚Wissensdatenbank' bei Intern").
 *
 * Welche Fakten zur Mail passen, entscheidet das Backend
 * (GET /api/v1/emails/{id}/betriebswissen, höchstens 6). Hier wird nur
 * gezeigt — und bei einem Fehler nie eine leere Liste vorgetäuscht.
 */
type Antwort = { fakten: Fakt[]; gesamt: number }

const klein = { fontSize: 12.5, color: "var(--text-muted)" } as const

export function BetriebswissenBlock({ emailId, tenantSlug }: { emailId: number; tenantSlug?: string | null }) {
  const q = useQuery<Antwort>({
    queryKey: ["betriebswissen-mail", emailId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${emailId}/betriebswissen`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: !!emailId,
    staleTime: 1000 * 60 * 5,
  })
  const href = wissensdatenbankHref(tenantSlug)

  return (
    <div className="ev2-meta-block" data-testid="betriebswissen">
      <div className="ev2-meta-label">Wissen zum Betrieb</div>
      {q.isLoading && <div style={klein}>wird geladen …</div>}
      {q.isError && <div style={klein}>Konnte nicht geladen werden.</div>}
      {q.data && (
        q.data.fakten.length === 0 ? (
          <div style={klein} data-testid="betriebswissen-leer">
            Kein passender Eintrag ({q.data.gesamt} Fakten gesamt) ·{" "}
            <a href={href} style={{ color: "var(--accent-color)" }}>Zur Wissensdatenbank</a>
          </div>
        ) : (
          <>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {q.data.fakten.map((f) => (
                <li key={f.id} style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}
                    title={f.bezug || undefined}>
                  <span className="ev2-meta-chip" style={{ fontSize: 11, padding: "1px 7px", marginRight: 6 }}>
                    {ART_ETIKETT[f.art] || f.art}
                  </span>
                  {f.aussage}
                  {f.dauer === "voruebergehend" && f.verfaellt_am && (
                    <span style={{ color: "var(--text-muted)" }}> · gilt bis {tagMonat(f.verfaellt_am)}</span>
                  )}
                </li>
              ))}
            </ul>
            <div style={{ fontSize: 12, marginTop: 8 }}>
              <a href={href} style={{ color: "var(--accent-color)" }}>Alle in der Wissensdatenbank</a>
            </div>
          </>
        )
      )}
    </div>
  )
}
